import { AttachMoney, PeopleOutlined } from '@mui/icons-material';
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { StyleObj } from '../../@types';
import { useCasinoRevenueStatistics, useCasinoUserStatistics } from '../../queries/casino';
import { formatNumberToStandard } from '../../utils';
import { mergeSx } from '../../utils/styles';

const styles: StyleObj = {
  container: {
    mb: 3,
  },
  content: {
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'stretch', md: 'center' },
    px: { xs: 2, md: 0 },
    gap: { xs: 1.5, md: 2 },
  },
  monthPicker: {
    width: { xs: '100%', md: 170 },
  },
  divider: {
    display: { xs: 'none', md: 'block' },
  },
  statCard: {
    backgroundColor: 'background.lighter',
    borderRadius: 2,
    px: 1.5,
    py: 1,
    border: '1px solid',
    borderColor: 'background.light',
  },
  statCardRevenue: {
    borderColor: 'primary.main',
  },
  statCardUsers: {
    borderColor: 'success.main',
  },
  statInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: { xs: 0.5, md: 4 },
  },
  statHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0.75,
    minWidth: { md: 130 },
  },
  statIcon: {
    fontSize: 22,
  },
  statIconRevenue: {
    color: 'primary.main',
  },
  statIconUsers: {
    color: 'success.main',
  },
  statValues: {
    flexDirection: 'column',
    alignItems: 'end',
    gap: 0.25,
  },
  percentText: {
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1,
  },
};

type StatCardProps = {
  title: string;
  value?: number | null;
  percent?: number | null;
  loading: boolean;
  valueFormat?: 'integer' | 'standard';
  icon: React.ReactNode;
  cardSx?: SxProps<Theme>;
};

const formatInteger = (value: number) => Intl.NumberFormat().format(Math.round(value));

const formatPercentChange = (value: number) => {
  if (value === 0) return '0.00%';

  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const StatCard = ({ title, value, percent, loading, valueFormat = 'standard', icon, cardSx }: StatCardProps) => {
  const numericValue = value ?? 0;
  const numericPercent = percent ?? 0;
  const formattedValue = valueFormat === 'integer' ? formatInteger(numericValue) : formatNumberToStandard(numericValue);
  const percentColor = numericPercent < 0 ? 'error.main' : numericPercent > 0 ? 'success.main' : 'text.secondary';

  return (
    <Stack sx={mergeSx(styles.statCard, cardSx)}>
      <Stack sx={styles.statInner}>
        <Stack sx={styles.statHeader}>
          {icon}
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        </Stack>
        <Stack sx={styles.statValues}>
          {loading ? (
            <Skeleton variant='text' width={40} height={20} />
          ) : (
            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
              }}
            >
              {formattedValue}
            </Typography>
          )}
          <Typography sx={mergeSx(styles.percentText, { color: percentColor })}>
            {formatPercentChange(numericPercent)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const CasinoReportsStatistics = () => {
  const [statisticsMonth, setStatisticsMonth] = useState<Dayjs>(() => dayjs().startOf('month'));

  const statisticsParams = useMemo(
    () => ({
      dateFrom: statisticsMonth.startOf('month').valueOf(),
      dateTo: statisticsMonth.endOf('month').valueOf(),
    }),
    [statisticsMonth]
  );

  const {
    data: revenueStatistics,
    isFetching: isRevenueFetching,
    isLoading: isRevenueLoading,
  } = useCasinoRevenueStatistics(statisticsParams);
  const {
    data: userStatistics,
    isFetching: isUsersFetching,
    isLoading: isUsersLoading,
  } = useCasinoUserStatistics(statisticsParams);

  const handleStatisticsMonthChange = useCallback((newDate: Dayjs | null) => {
    if (!newDate) return;

    setStatisticsMonth(newDate.startOf('month'));
  }, []);

  const revenueLoading = isRevenueFetching || isRevenueLoading;
  const usersLoading = isUsersFetching || isUsersLoading;

  return (
    <Box sx={styles.container}>
      <Stack sx={styles.content}>
        <DatePicker
          label='Month'
          value={statisticsMonth}
          onChange={handleStatisticsMonthChange}
          views={['year', 'month']}
          openTo='month'
          format='MMMM YYYY'
          maxDate={dayjs()}
          slotProps={{
            textField: {
              size: 'small',
              sx: styles.monthPicker,
            },
          }}
        />
        <Divider flexItem orientation='vertical' sx={styles.divider} />
        <StatCard
          title='Total Revenue'
          loading={revenueLoading}
          value={revenueStatistics?.revenue}
          percent={revenueStatistics?.percentage}
          icon={<AttachMoney sx={mergeSx(styles.statIcon, styles.statIconRevenue)} />}
          cardSx={styles.statCardRevenue}
        />
        <Divider flexItem orientation='vertical' sx={styles.divider} />
        <StatCard
          title='Total Active Users'
          loading={usersLoading}
          value={userStatistics?.activeUsers}
          percent={userStatistics?.percentage}
          valueFormat='integer'
          icon={<PeopleOutlined sx={mergeSx(styles.statIcon, styles.statIconUsers)} />}
          cardSx={styles.statCardUsers}
        />
      </Stack>
    </Box>
  );
};

export default CasinoReportsStatistics;
